import { Result } from '@mapbox/mapbox-gl-geocoder';

/**
 * Return object that follows mapbox-gl-geocoder expected result format
 */
const getCoordinateFeature = (lng: number, lat: number): Result => {
  return {
    type: 'Feature',
    properties: {},
    center: [lng, lat],
    geometry: {
      type: 'Point',
      coordinates: [lng, lat]
    },
    text: `Lat: ${lat} Lng: ${lng}`,
    place_name: `Lat: ${lat} Lng: ${lng}`,
    place_type: ['coordinate']
  } as Result;
};

/**
 * Given a query in the form "lng, lat" or "lat, lng" returns the matching geographic coordinate(s) as search results in carmen geojson format.
 * @url https://docs.mapbox.com/mapbox-gl-js/example/mapbox-gl-geocoder-accept-coordinates/
 *
 * @param query { string } - query string from mapbox-gl-geocoder
 */
export const coordinatesGeocoder = (query: string): Result[] => {
  // Match anything which looks like decimal degrees coordinate pair.
  const matches = getCoordinatePairs(query);
  if (!matches) {
    return [];
  }

  const coord1 = Number(matches[1]);
  const coord2 = Number(matches[2]);
  const geoCoords = [];

  if (coord1 < -90 || coord1 > 90) {
    // must be lng, lat
    geoCoords.push(getCoordinateFeature(coord1, coord2));
  }

  if (coord2 < -90 || coord2 > 90) {
    // must be lat, lng
    geoCoords.push(getCoordinateFeature(coord2, coord1));
  }

  if (geoCoords.length === 0) {
    // else could be either lng, lat or lat, lng
    geoCoords.push(getCoordinateFeature(coord1, coord2));
    geoCoords.push(getCoordinateFeature(coord2, coord1));
  }

  return geoCoords;
};

/**
 * Return matches for anything which looks like decimal degrees coordinate pair.
 * @param {string} value String that might contain coordinate pairs
 */
export const getCoordinatePairs = (value?: string | null) => {
  return value?.match(
    /^[ ]*(?:Lat: ?)?(-?\d+\.?\d*)[, ]+(?:Lng: ?)?(-?\d+\.?\d*)[ ]*$/i
  );
};

/**
 * Update the place name if coordinates are found to the format we want to display to the
 * user, if no coordinates are found return the original place name.
 * @param {string} placeName - place name from mapbox-gl-geocoder suggestion
 */
export const updatePlaceName = (placeName: string) => {
  const coordinates = getCoordinatePairs(placeName);
  if (coordinates) {
    const coord1 = coordinates[1];
    const coord2 = coordinates[2];
    return `Lat: ${coord1}, Lng: ${coord2}`;
  }

  return placeName;
};
